import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, of } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'normal' | 'god';
  type: 'employee' | 'client' | 'god';
  status?: 'active' | 'inactive';
  avatar?: string;
}

export interface LoginResponse {
  access_token: string;
  user: User;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly TOKEN_KEY = 'access_token';
  private readonly USER_KEY = 'current_user';

  private currentUserSubject = new BehaviorSubject<User | null>(this.getCurrentUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  // Método para inyectar CompanyContextService (evitar dependencia circular)
  private companyContextService?: any;

  setCompanyContextService(service: any): void {
    this.companyContextService = service;
  }

  googleLogin(credential: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/google`, { credential })
      .pipe(
        tap(response => {
          this.setToken(response.access_token);
          this.setUser(response.user);
          this.currentUserSubject.next(response.user);
        })
      );
  }

  register(firstName: string, lastName: string, email: string, password: string): Observable<LoginResponse> {
    // Registro publico crea usuarios inactivos (pendientes de aprobacion) pero
    // recibe JWT. El AuthGuard rutea inactivos a /dashboard/pending-approval.
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/register`, { firstName, lastName, email, password })
      .pipe(
        tap(response => {
          this.setToken(response.access_token);
          this.setUser(response.user);
          this.currentUserSubject.next(response.user);
        })
      );
  }

  loginWithPassword(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/login`, { email, password })
      .pipe(
        tap(response => {
          this.setToken(response.access_token);
          this.setUser(response.user);
          this.currentUserSubject.next(response.user);
        })
      );
  }

  logout(): void {
    // Limpiar contexto de empresa si existe
    if (this.companyContextService) {
      this.companyContextService.clearOnLogout();
    }

    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!token && !!user;
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'admin';
  }

  isEmployee(): boolean {
    const user = this.getCurrentUser();
    return user?.type === 'employee';
  }

  isClient(): boolean {
    const user = this.getCurrentUser();
    return user?.type === 'client';
  }

  isGod(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'god' || user?.type === 'god';
  }

  validateToken(): Observable<{ valid: boolean; user: User }> {
    if (!this.getToken()) {
      return of({ valid: false, user: null as any });
    }

    return this.http.get<{ valid: boolean; user: User }>(`${this.API_URL}/auth/validate`)
      .pipe(
        tap(response => {
          if (response.valid && response.user) {
            this.setUser(response.user);
            this.currentUserSubject.next(response.user);
          } else {
            this.logout();
          }
        })
      );
  }
}
